import { GeneralError, User, UserDetails, UserRepositoryType } from '51trips-frontend-core'
import axios from 'axios'
import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore'
import { Observable } from 'rxjs'

import { auth, db } from '../firebase'
import generalErrorConverter from './converters/GeneralErrorConverter'
import userConverter from './converters/UserConverter'
import { RemoteConfigurationRepository } from './RemoteConfigurationRepository'

const remoteConfigurationRepository = new RemoteConfigurationRepository()

export class UserRepository implements UserRepositoryType {
  whoIAm(): Observable<UserDetails | null> {
    return new Observable((subscriber) => {
      const firebaseUser = auth.currentUser
      if (!firebaseUser) {
        subscriber.next(null)
        subscriber.complete()
        return
      }

      firebaseUser
        .getIdToken()
        .then((token) => {
          const url = `${remoteConfigurationRepository.tripAPI}/users/whoami`
          return axios.get<UserDetails>(url, {
            headers: { Authorization: `Bearer ${token}` }
          })
        })
        .then((response) => {
          subscriber.next(response.data)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseAuthError(
              error,
              'Fetching authenticated user (whoAmI) failed'
            )
          )
        })
    })
  }

  /** Whether `users/{userID}` exists — phone sign-in can authenticate someone with no profile yet. */
  userExists(userID: string): Observable<boolean> {
    return new Observable((subscriber) => {
      getDoc(doc(db, 'users', userID))
        .then((documentSnapshot) => {
          subscriber.next(documentSnapshot.exists())
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `checking whether user ${userID} exists failed`
            )
          )
        })
    })
  }

  /**
   * Whether another `users` doc already has this email. Emails are stored as typed,
   * so the lowercased spelling is checked too.
   */
  isEmailInUse(email: string): Observable<boolean> {
    return new Observable((subscriber) => {
      const trimmed = email.trim()
      const spellings = [...new Set([trimmed, trimmed.toLowerCase()])]
      getDocs(query(collection(db, 'users'), where('email', 'in', spellings), limit(1)))
        .then((querySnapshot) => {
          subscriber.next(!querySnapshot.empty)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              'checking whether an email is already in use failed'
            )
          )
        })
    })
  }

  createUser(
    userID: string,
    email?: string | undefined,
    fullName?: string | undefined,
    deviceLanguageTag?: string | undefined
  ): Observable<string> {
    return new Observable((subscriber) => {
      setDoc(doc(db, 'users', userID), {
        createdAt: Date.now(),
        email,
        fullName,
        deviceLanguageTag
      })
        .then(() => {
          subscriber.next(userID)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `fetching user for ${userID} failed`
            )
          )
        })
    })
  }

  fetchUser(userID: string): Observable<User> {
    return new Observable((subscriber) => {
      getDoc(doc(db, 'users', userID))
        .then((documentSnapshot) => {
          if (documentSnapshot.exists()) {
            const user = userConverter.fromFirestore(documentSnapshot)
            if (user) {
              subscriber.next(user)
              subscriber.complete()
            } else {
              subscriber.error(
                new GeneralError(
                  `Document snapshot at path ${documentSnapshot.ref.path} when loading user doesn't exist`
                )
              )
            }
          } else {
            subscriber.error(
              new GeneralError(
                `Document snapshot at path ${documentSnapshot.ref.path} when fetching user doesn't exist`
              )
            )
          }
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `fetching user for ${userID} failed`
            )
          )
        })
    })
  }

  loadUser(userID: string): Observable<User> {
    return new Observable((subscriber) => {
      getDoc(doc(db, 'users', userID))
        .then((documentSnapshot) => {
          if (documentSnapshot.exists()) {
            const user = userConverter.fromFirestore(documentSnapshot)
            if (user) {
              subscriber.next(user)
              subscriber.complete()
            } else {
              subscriber.error(
                new GeneralError(
                  `Document snapshot at path ${documentSnapshot.ref.path} when loading user doesn't exist`
                )
              )
            }
          } else {
            subscriber.error(
              new GeneralError(
                `Document snapshot at path ${documentSnapshot.ref.path} when loading user doesn't exist`
              )
            )
          }
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Loading user for ${userID} failed`
            )
          )
        })
    })
  }

  fetchUserWithEmail(userEmail: string): Observable<User> {
    return new Observable((subscriber) => {
      getDocs(query(collection(db, 'users'), where('email', '==', userEmail)))
        .then((querySnapshot) => {
          let user
          const [userData] = querySnapshot.docs
          if (userData && userData.exists()) {
            user = userConverter.fromFirestore(userData)
          }
          if (user) {
            subscriber.next(user)
            subscriber.complete()
          } else {
            subscriber.error(
              new GeneralError(
                `Document snapshot at path ${userData.ref.path} when loading user doesn't exist`
              )
            )
          }
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Loading user for ${userEmail} failed`
            )
          )
        })
    })
  }

  loadUserRole(userID: string): Observable<boolean> {
    return new Observable((subscriber) => {
      getDoc(doc(db, 'users', userID))
        .then((documentSnapshot) => {
          if (documentSnapshot.exists()) {
            const user = userConverter.fromFirestore(documentSnapshot)
            if (user) {
              subscriber.next(user.isAdmin)
              subscriber.complete()
            } else {
              subscriber.error(
                new GeneralError(
                  `Document snapshot at path ${documentSnapshot.ref.path} when loading user role doesn't exist`
                )
              )
            }
          } else {
            subscriber.error(
              new GeneralError(
                `Document snapshot at path ${documentSnapshot.ref.path} when loading user role doesn't exist`
              )
            )
          }
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Loading user role for ${userID} failed`
            )
          )
        })
    })
  }

  loadUserFullName(userID: string): Observable<string> {
    return new Observable((subscriber) => {
      getDoc(doc(db, 'users', userID))
        .then((documentSnapshot) => {
          const userData = documentSnapshot.data()
          if (documentSnapshot.exists() && userData && typeof userData.fullName === 'string') {
            subscriber.next(userData.fullName)
            subscriber.complete()
          } else {
            subscriber.error(
              new GeneralError(
                `Document snapshot at path ${documentSnapshot.ref.path} when loading user full name doesn't exist`
              )
            )
          }
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Loading user full name for ${userID} failed`
            )
          )
        })
    })
  }

  loadUserEmail(userID: string): Observable<string> {
    return new Observable((subscriber) => {
      getDoc(doc(db, 'users', userID))
        .then((documentSnapshot) => {
          const userData = documentSnapshot.data()
          if (documentSnapshot.exists() && userData && typeof userData.email === 'string') {
            subscriber.next(userData.email)
            subscriber.complete()
          } else {
            subscriber.error(
              new GeneralError(
                `Document snapshot at path ${documentSnapshot.ref.path} when loading user email doesn't exist`
              )
            )
          }
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Loading user email for ${userID} failed`
            )
          )
        })
    })
  }

  loadUserProfileImage(userID: string): Observable<string | undefined> {
    return new Observable((subscriber) => {
      getDoc(doc(db, 'users', userID))
        .then((documentSnapshot) => {
          const userData = documentSnapshot.data()
          if (
            documentSnapshot.exists() &&
            userData &&
            (typeof userData.profileImageURL === 'string' ||
              typeof userData.profileImageURL === 'undefined')
          ) {
            subscriber.next(userData.profileImageURL)
            subscriber.complete()
          } else {
            subscriber.error(
              new GeneralError(
                `Document snapshot at path ${documentSnapshot.ref.path} when loading user profile image URL doesn't exist`
              )
            )
          }
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Loading user profile image for ${userID} failed: ${error.message}`
            )
          )
        })
    })
  }

  loadUserVisitedCountriesIDs(userID: string): Observable<string[]> {
    return new Observable((subscriber) => {
      getDoc(doc(db, 'users', userID))
        .then((documentSnapshot) => {
          let visitedCountriesIDs: string[] = []
          const userData = documentSnapshot.data()
          if (
            documentSnapshot.exists() &&
            userData &&
            Array.isArray(userData.visitedCountriesIDs) &&
            userData.visitedCountriesIDs.length &&
            userData.visitedCountriesIDs.every(
              (visitedCountry) => typeof visitedCountry === 'string'
            )
          ) {
            visitedCountriesIDs = userData.visitedCountriesIDs
          }
          subscriber.next(visitedCountriesIDs)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Loading user visited countries for ${userID} failed`
            )
          )
        })
    })
  }

  loadUserWantToVisitCountriesIDs(userID: string): Observable<string[]> {
    return new Observable((subscriber) => {
      getDoc(doc(db, 'users', userID))
        .then((documentSnapshot) => {
          let wantToVisitCountriesIDs: string[] = []
          const userData = documentSnapshot.data()
          if (
            documentSnapshot.exists() &&
            userData &&
            Array.isArray(userData.wantToVisitCountriesIDs) &&
            userData.wantToVisitCountriesIDs.length &&
            userData.wantToVisitCountriesIDs.every(
              (wantToVisitCountry) => typeof wantToVisitCountry === 'string'
            )
          ) {
            wantToVisitCountriesIDs = userData.wantToVisitCountriesIDs
          }
          subscriber.next(wantToVisitCountriesIDs)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Loading want to visit countries IDs for user ${userID} failed`
            )
          )
        })
    })
  }

  loadUserVisitedWondersIDs(userID: string): Observable<string[]> {
    return new Observable((subscriber) => {
      getDoc(doc(db, 'users', userID))
        .then((documentSnapshot) => {
          let visitedWondersIDs: string[] = []
          const userData = documentSnapshot.data()
          if (
            documentSnapshot.exists() &&
            userData &&
            Array.isArray(userData.visitedWondersIDs) &&
            userData.visitedWondersIDs.length &&
            userData.visitedWondersIDs.every(
              (visitedWonderID) => typeof visitedWonderID === 'string'
            )
          ) {
            visitedWondersIDs = userData.visitedWondersIDs
          }
          subscriber.next(visitedWondersIDs)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Loading user visited wonders for ${userID} failed`
            )
          )
        })
    })
  }

  loadUserWantToVisitWondersIDs(userID: string): Observable<string[]> {
    return new Observable((subscriber) => {
      getDoc(doc(db, 'users', userID))
        .then((documentSnapshot) => {
          let wantToVisitWondersIDs: string[] = []
          const userData = documentSnapshot.data()
          if (
            documentSnapshot.exists() &&
            userData &&
            Array.isArray(userData.wantToVisitWondersIDs) &&
            userData.wantToVisitWondersIDs.length &&
            userData.wantToVisitWondersIDs.every(
              (wantToVisitWonder) => typeof wantToVisitWonder === 'string'
            )
          ) {
            wantToVisitWondersIDs = userData.wantToVisitWondersIDs
          }
          subscriber.next(wantToVisitWondersIDs)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Loading want to visit wonders IDs for user ${userID} failed`
            )
          )
        })
    })
  }

  loadUserAffiliateCode(userID: string): Observable<string | undefined> {
    return new Observable((subscriber) => {
      getDoc(doc(db, 'users', userID))
        .then((documentSnapshot) => {
          if (!documentSnapshot.exists()) {
            subscriber.error(
              new GeneralError(
                `Document snapshot at path ${documentSnapshot.ref.path} when loading user affiliate code doesn't exist`
              )
            )
          }
          const userData = documentSnapshot.data()
          subscriber.next(userData?.affiliateCode)
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Loading user affiliate code for ${userID} failed: ${error.message}`
            )
          )
        })
    })
  }

  addTrip(tripID: string, userID: string): Observable<string> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), {
        tripsIDs: arrayUnion(tripID)
      })
        .then(() => {
          subscriber.next(tripID)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Adding trip with ${tripID} to user with ${userID} failed`
            )
          )
        })
    })
  }

  deleteTrip(tripID: string, userID: string): Observable<string> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), {
        tripsIDs: arrayRemove(tripID)
      })
        .then(() => {
          subscriber.next(tripID)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Removing trip with ${tripID} from user with ${userID} failed`
            )
          )
        })
    })
  }

  deleteAddress(userID: string, addressID: string): Observable<string> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), {
        addressesIDs: arrayRemove(addressID)
      })
        .then(() => {
          subscriber.next(addressID)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Removing address with ${addressID} from user with ${userID} failed`
            )
          )
        })
    })
  }

  deleteOrder(userID: string, orderID: string): Observable<string> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), {
        ordersIDs: arrayRemove(orderID)
      })
        .then(() => {
          subscriber.next(orderID)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Removing order with ${orderID} from user with ${userID} failed`
            )
          )
        })
    })
  }

  setUserFullName(userID: string, fullName: string): Observable<void> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), {
        fullName
      })
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Setting full name for user ${userID} failed`
            )
          )
        })
    })
  }

  setUserProfileImage(userID: string, imageURL: string): Observable<string> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), {
        profileImageURL: imageURL
      })
        .then(() => {
          subscriber.next(imageURL)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Setting profile image for user ${userID} failed`
            )
          )
        })
    })
  }

  setUserVisitedCountriesIDs(userID: string, visitedCountriesIDs: string[]): Observable<void> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), { visitedCountriesIDs })
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Setting visited countries IDs for user ${userID} failed`
            )
          )
        })
    })
  }

  setUserWantToVisitCountriesIDs(
    userID: string,
    wantToVisitCountriesIDs: string[]
  ): Observable<void> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), { wantToVisitCountriesIDs })
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Setting want to visit countries IDs for user ${userID} failed`
            )
          )
        })
    })
  }

  setUserVisitedWondersIDs(userID: string, visitedWondersIDs: string[]): Observable<void> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), { visitedWondersIDs })
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Setting visited wonders IDs for user ${userID} failed`
            )
          )
        })
    })
  }

  setUserWantToVisitWondersIDs(userID: string, wantToVisitWondersIDs: string[]): Observable<void> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), { wantToVisitWondersIDs })
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Setting want to visit wonders IDs for user ${userID} failed`
            )
          )
        })
    })
  }

  setUserAccomplishedBucketListItem(userID: string, itemID: string): Observable<void> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), {
        accomplishedBucketListItemsIDs: arrayUnion(itemID)
      })
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Setting accomplished bucket list itemID for user ${userID} failed`
            )
          )
        })
    })
  }

  setUserIsPrivacyPolicyAccepted(
    userID: string,
    isPrivacyPolicyAccepted: boolean
  ): Observable<void> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), { isPrivacyPolicyAccepted })
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Setting isPrivacyPolicyAccepted for user ${userID} failed`
            )
          )
        })
    })
  }

  setUserIsCommercialConsentAccepted(
    userID: string,
    isCommercialConsentAccepted: boolean
  ): Observable<void> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), { isCommercialConsentAccepted })
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Setting isCommercialConsentAccepted for user ${userID} failed`
            )
          )
        })
    })
  }

  setUserAddress(userID: string, addressID: string): Observable<string> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), {
        addressesIDs: arrayUnion(addressID)
      })
        .then(() => {
          subscriber.next(addressID)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Setting addressID ${addressID} for user ${userID} failed`
            )
          )
        })
    })
  }

  setUserOrder(userID: string, orderID: string): Observable<string> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), {
        ordersIDs: arrayUnion(orderID)
      })
        .then(() => {
          subscriber.next(orderID)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Setting orderID ${orderID} for user ${userID} failed`
            )
          )
        })
    })
  }

  setUserAppVersion(userID: string, appVersion: string): Observable<void> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), { appVersion })
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Setting app version for user ${userID} failed`
            )
          )
        })
    })
  }

  deleteUserAccomplishedBucketListItem(userID: string, itemID: string): Observable<void> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), {
        accomplishedBucketListItemsIDs: arrayRemove(itemID)
      })
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Deleting accomplished bucket list itemID for user ${userID} failed`
            )
          )
        })
    })
  }

  setUserNumberOfBucketListItems(userID: string, incrementNumber: number): Observable<void> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), {
        numberOfBucketListItems: increment(incrementNumber)
      })
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Setting increment number of bucket list items for user ${userID} failed`
            )
          )
        })
    })
  }

  setUserDeviceLanguageTag(userID: string, deviceLanguageTag: string): Observable<void> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), { deviceLanguageTag })
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Setting device language tag for user ${userID} failed`
            )
          )
        })
    })
  }

  onUserChanged(userID: string): Observable<User> {
    return new Observable((subscriber) => {
      return onSnapshot(
        doc(db, 'users', userID),
        (documentSnapshot) => {
          const user = userConverter.fromFirestore(documentSnapshot)
          if (user) {
            subscriber.next(user)
          } else {
            subscriber.error(
              new GeneralError(
                `Document snapshot at path ${documentSnapshot.ref.path} for changing user doesn't exist`
              )
            )
          }
        },
        (error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Error occurred when observing changes on ${userID}`
            )
          )
        },
        () => {
          subscriber.complete()
        }
      )
    })
  }

  deleteUser(userID: string): Observable<void> {
    return new Observable((subscriber) => {
      deleteDoc(doc(db, 'users', userID))
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Deleting user with ${userID} failed: ${error.message}`
            )
          )
        })
    })
  }

  deleteUserProfileImage(userID: string): Observable<void> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), { profileImageURL: deleteField() })
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Deleting user profile image with userID ${userID} failed: ${error.message}`
            )
          )
        })
    })
  }

  setUserMessagingToken(userID: string, messagingToken: string): Observable<void> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), {
        messagingTokens: arrayUnion(messagingToken)
      })
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Add messaging token to userID ${userID} failed: ${error.message}`
            )
          )
        })
    })
  }

  setUserInstallationID(userID: string, installationID: string): Observable<void> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), {
        installationsIDs: arrayUnion(installationID)
      })
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Add installation ID to userID ${userID} failed: ${error.message}`
            )
          )
        })
    })
  }

  setUserAffiliateCode(userID: string, affiliateCode: string): Observable<void> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), { referralAgencyID: affiliateCode })
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Add referral code to userID ${userID} failed: ${error.message}`
            )
          )
        })
    })
  }

  // Records the market the user came through (from the landing page's `mkt` query
  // param, e.g. "840"/US, "826"/UK). setDoc+merge is used instead of updateDoc so
  // it is safe even if the user doc write is still racing on a fresh registration.
  setUserMarket(userID: string, market: string): Observable<void> {
    return new Observable((subscriber) => {
      setDoc(doc(db, 'users', userID), { market }, { merge: true })
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Setting market for user ${userID} failed: ${error.message}`
            )
          )
        })
    })
  }

  setUserPhoneNumber(userID: string, phoneNumber: string): Observable<void> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'users', userID), { phoneNumber })
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Add phone number to userID ${userID} failed: ${error.message}`
            )
          )
        })
    })
  }
}
